'use client';

/**
 * UiShell.tsx
 *
 * Global layout-wrapper med Header, side-nav og paneler.
 * Bruker egne React-komponenter (src/components/ui-shell/) — ingen
 * runtime-avhengighet av @carbon/web-components.
 *
 * Komponenten tar imot `children` som rendres i innholdsområdet.
 *
 * Innhold:
 * 1. Header — topplinje med logo, navigasjon og actions
 * 2. Paneler — utvidbare paneler (search, notification, switcher)
 * 3. Side-nav — responsiv sidemeny (rollebasert fra navigation config)
 * 4. Side-innhold — rute-spesifikt innhold (children)
 *
 * Navigasjon er rollebasert:
 * - src/lib/navigation.ts definerer meny-elementer med `roles`
 * - src/hooks/useAuth.ts returnerer innlogget bruker
 * - UiShell filtrerer menyen basert på brukerens rolle
 */

import { useCallback, useState, ReactNode, useSyncExternalStore } from 'react';
import { usePathname } from 'next/navigation';
import styles from './ui-shell.module.css';

// Notifikasjonsstore — uleste-teller for dott på varsel-ikonet
import {
  subscribeNotifications,
  getUnreadCountSnapshot,
} from '@/lib/notifications';

// Full-height notification drawer (erstatter HeaderPanel for varsler)
import NotificationDrawer from '@/sections/NotificationDrawer/NotificationDrawer';

// AI-chat-drawer (frakoblet placeholder — kobles til backend senere)
import AiChatDrawer from '@/sections/AiChatDrawer/AiChatDrawer';
import { realAiSettings } from '@/sections/AiChatDrawer/mcp-api';

// Egne UI Shell-komponenter (ingen runtime-avhengighet av Carbon)
import {
  Header,
  HeaderMenuButton,
  HeaderName,
  HeaderNav,
  HeaderNavItem,
  HeaderGlobalAction,
  HeaderPanel,
  SideNav,
  SideNavLink,
  SideNavMenu,
  SideNavMenuItem,
  Switcher,
  SwitcherItem,
  SwitcherDivider,
} from '@/components/ui-shell';

// Egne SVG-ikon-komponenter (ingen runtime-avhengighet av Carbon)
import { SearchIcon, NotificationIcon, SwitcherIcon } from '@/components/icons';
// AI-ikon fra @carbon/icons-react (kun for den nye header-actionen)
import { Ai as AiIcon, Light as LightIcon, Asleep as DarkIcon } from '@carbon/icons-react';
import { useTheme } from '@/hooks/useTheme';

// Navigation config og auth
import { headerNav, sideNav, isNavMenu, getHomePath, SHOW_ALL_MENU_ITEMS } from '@/lib/navigation';
import type { NavItem, SideNavItem } from '@/lib/navigation';
import type { AiActivity } from '@/types/ai';
import { useAuth } from '@/hooks/useAuth';
import { hasRole, getForhandlerEntries, getRoleDepartmentNames } from '@/lib/auth';
import {
  getActiveForhandlerId,
  setActiveForhandlerId,
} from '@/lib/active-forhandler';
import { capitalizeFirstLetter } from '@/utils/format';

export default function UiShell({ children }: { children: ReactNode }) {
  const pathname = usePathname();
  const { user } = useAuth();
  const { theme, toggle: toggleTheme } = useTheme();
  const [sideNavExpanded, setSideNavExpanded] = useState(false);
  const [searchPanelOpen, setSearchPanelOpen] = useState(false);
  const [notificationPanelOpen, setNotificationPanelOpen] = useState(false);
  const [switcherPanelOpen, setSwitcherPanelOpen] = useState(false);
  // AI-aktiv tilstand: AI-chat-drawer er åpen og header får blå
  // gradient-bakgrunn (som Carbon AI Chat-launcheren).
  const [aiActive, setAiActive] = useState(false);
  // Hva AI-en gjør (kobler til / tenker / skriver / ferdig) — animasjoner
  const [aiActivity, setAiActivity] = useState<AiActivity>('idle');
  // Stabil referanse — drawer-ens fokus-effekt avhenger av onClose
  const closeAiChat = useCallback(() => setAiActive(false), []);

  // === Notifikasjoner — uleste-teller for dott på varsel-ikonet ===
  // useSyncExternalStore: abonnerer på store, re-render ved endring.
  // Snapshot-funksjonen er stabil (modul-nivå), så ingen loop.
  // (Varsellisten selv bor i NotificationDrawer-komponenten.)
  const unreadCount = useSyncExternalStore(
    subscribeNotifications,
    getUnreadCountSnapshot
  );

  // Aktiv forhandler — leses fra localStorage (kun ID lagres, per konvensjon).
  // Lazy initializer med window-guard: localStorage finnes ikke under SSR,
  // så vi leser kun på klienten. Ingen setState i effect (unngår cascading renders).
  const [activeForhandlerId, setActiveForhandlerIdState] = useState<string | null>(() =>
    typeof window === 'undefined' ? null : getActiveForhandlerId()
  );

  // Hjelpefunksjon: lukker alle paneler, evt. åpner det valgte.
  // NB: Varsler markeres IKKE som lest ved åpning lenger — drawer-en har
  // eksplisitt "Merk alle som lest"-knapp (som referanse-drawer).
  const togglePanel = (panel: 'ai' | 'search' | 'notification' | 'switcher') => {
    setAiActive(panel === 'ai' ? !aiActive : false);
    setSearchPanelOpen(panel === 'search' ? !searchPanelOpen : false);
    setNotificationPanelOpen(panel === 'notification' ? !notificationPanelOpen : false);
    setSwitcherPanelOpen(panel === 'switcher' ? !switcherPanelOpen : false);
  };

  // Hjelpefunksjon: sjekker om en lenke er aktiv basert på pathname
  const isActive = (href: string) => {
    if (href === '/') return pathname === '/';
    return pathname === href || pathname.startsWith(href + '/');
  };

  // Har brukeren tilgang til et menypunkt? (SHOW_ALL_MENU_ITEMS i navigation.ts viser alt midlertidig)
  const canSee = (roleIds: string[]) => SHOW_ALL_MENU_ITEMS || hasRole(user, roleIds);

  // Avdelingene rollen har tilgang til (JWT: role_department_names) avgjør hvilke rotmenyer som vises
  const departments = getRoleDepartmentNames();

  // Filtrerer header-nav basert på brukerens role_id og avdelinger
  const visibleHeaderNav: NavItem[] = headerNav.filter((item) =>
    (!item.department || departments.includes(item.department)) && canSee(item.role_ids)
  );

  // Forhandler-(id, navn)-par fra JWT — vises i switcher-panelet (as-is)
  const forhandlerEntries = getForhandlerEntries();

  // Håndterer valg av forhandler: lagrer KUN ID-en i localStorage
  // (nøkkel "aktiv_forhandler") og oppdaterer aktiv-markeringen.
  const handleSelectForhandler = (id: string) => {
    setActiveForhandlerId(id);
    setActiveForhandlerIdState(id);
  };

  // Filtrerer side-nav: rotmenyer vises bare hvis rollen har menyens avdeling. Unntak: alwaysVisible (User settings). Menyer uten avdeling (inAds) er skjult.
  const visibleSideNav: SideNavItem[] = sideNav.filter((item) => {
    if (isNavMenu(item)) {
      if (!item.alwaysVisible && !(item.department && departments.includes(item.department))) return false;
      return canSee(item.role_ids);
    }
    // Enkeltpunkt med avdeling (inAdmin Dashboard): bare for roller som har avdelingen
    if (item.department && !departments.includes(item.department)) return false;
    return canSee(item.role_ids);
  });

  return (
    <>
      {/* ================================================================= */}
      {/* 1. Header — topplinje med logo, navigasjon og actions             */}
      {/* ================================================================= */}
      <Header ariaLabel="Hovednavigasjon" aiActive={aiActive} aiActivity={aiActivity}>
        <HeaderMenuButton
          active={sideNavExpanded}
          onClick={() => setSideNavExpanded(!sideNavExpanded)}
        />
        {/* Logo-lenken peker på /dashboard (IKKE rotdomenet) — rotdomenet
            er kun en gateway og skal ikke være et navigasjonsmål */}
        <HeaderName
          href={getHomePath(departments)}
          prefixImageSrc="/Logo/inSuiteWhiteFontSVG.png"
          prefixImageAlt="inSuite (Alpha stage)"
        >
          {/* Brukernavn fra JWT (via AuthContext) — as-is, kun formatering for visning */}
          {user && `Hei, ${capitalizeFirstLetter(user.username)}!`}
        </HeaderName>
        <HeaderNav ariaLabel="Hovednavigasjon">
          {visibleHeaderNav.map((item) => (
            <HeaderNavItem key={item.href} href={item.href} active={isActive(item.href)}>
              {item.label}
            </HeaderNavItem>
          ))}
        </HeaderNav>

        {/* Global actions (helt til høyre) — AI, Search, Notification, Switcher */}
        <div className={styles.actions}>
          <HeaderGlobalAction
            icon={<AiIcon size={20} />}
            label="AI"
            active={aiActive}
            pressed={aiActive}
            onClick={() => togglePanel('ai')}
          />
          <HeaderGlobalAction
            icon={<SearchIcon size={20} />}
            label="Søk"
            active={searchPanelOpen}
            onClick={() => togglePanel('search')}
          />
          <HeaderGlobalAction
            icon={<NotificationIcon size={20} />}
            label={
              unreadCount > 0
                ? `Varsler (${unreadCount} uleste)`
                : 'Varsler'
            }
            active={notificationPanelOpen}
            onClick={() => togglePanel('notification')}
          >
            {/* Ulest-dott (Carbon Badge indicator without number — Status
                Indicator Pattern). Dotten er dekorativ; uleste-tallet er
                lesbar for skjermlesere via aria-label over. */}
            {unreadCount > 0 && <span className={styles.unreadDot} />}
          </HeaderGlobalAction>
          <HeaderGlobalAction
            icon={theme === 'dark' ? <LightIcon size={20} /> : <DarkIcon size={20} />}
            label={theme === 'dark' ? 'Lyst tema' : 'Mørkt tema'}
            onClick={toggleTheme}
          />
          <HeaderGlobalAction
            icon={<SwitcherIcon size={20} />}
            label="App switcher"
            active={switcherPanelOpen}
            onClick={() => togglePanel('switcher')}
          />
        </div>
      </Header>

      {/* ================================================================= */}
      {/* 2. Paneler — utvidbare paneler knyttet til global actions          */}
      {/* ================================================================= */}

      {/* AI-chat-drawer — glir inn fra høyre (Carbon AI Chat sidebar) */}
      <AiChatDrawer open={aiActive} onClose={closeAiChat} sideNavExpanded={sideNavExpanded}
        onActivityChange={setAiActivity} settings={realAiSettings}
      />

      {/* Search-panel */}
      <HeaderPanel expanded={searchPanelOpen} ariaLabel="Søkepanel">
        <div className={styles.panelContent}>
          <input
            type="search"
            className={styles.searchInput}
            placeholder="Søk i innhold…"
            aria-label="Søk"
          />
        </div>
      </HeaderPanel>

      {/* Notification-drawer — full-height drawer fra høyre (Carbon
          notification-panel-mønsteret). Erstatter HeaderPanel for varsler.
          Åpnes/lukkes av varsel-ikonet; Escape/klikk-utenfor lukker også. */}
      <NotificationDrawer
        open={notificationPanelOpen}
        onClose={() => setNotificationPanelOpen(false)}
      />

      {/* App switcher-panel */}
      <HeaderPanel expanded={switcherPanelOpen} ariaLabel="App switcher">
        <div className={styles.panelContent}>
          <h3 className={styles.panelTitle}>Bytt forhandler</h3>
          <Switcher>
            {/* Forhandler-(id, navn)-par fra JWT (forhandler_name_cosmetic) — as-is.
                Klikk lagrer KUN ID-en i localStorage ("aktiv_forhandler").
                Aktiv forhandler markeres med grønt status-symbol. */}
            {forhandlerEntries.map((entry) => (
              <SwitcherItem
                key={entry.id}
                active={entry.id === activeForhandlerId}
                onClick={() => handleSelectForhandler(entry.id)}
              >
                {entry.name}
              </SwitcherItem>
            ))}
            <SwitcherDivider />
          </Switcher>
        </div>
      </HeaderPanel>

      {/* ================================================================= */}
      {/* 3. Side-nav — responsiv sidemeny (rollebasert fra config)         */}
      {/* ================================================================= */}
      <SideNav expanded={sideNavExpanded} onExpand={() => setSideNavExpanded(true)} ariaLabel="Sidemeny">
        {visibleSideNav.map((item, index) => {
          if (isNavMenu(item)) {
            // Filtrer under-elementer basert på role_id
            const visibleChildren = item.children.filter((child) =>
              canSee(child.role_ids)
            );
            // Hopp over menyen hvis ingen under-elementer er synlige
            if (visibleChildren.length === 0) return null;
            // Åpen side i menyen: den lengste href-en som passer (/in-rental skal ikke matche /in-rental/vehicles)
            const activeHref = visibleChildren
              .filter((child) => isActive(child.href))
              .sort((a, b) => b.href.length - a.href.length)[0]?.href;
            return (
              <SideNavMenu key={`menu-${index}`} title={item.title} icon={item.icon} current={activeHref !== undefined}>
                {visibleChildren.map((child) => (
                  <SideNavMenuItem key={child.href} href={child.href} active={child.href === activeHref}>
                    {child.label}
                  </SideNavMenuItem>
                ))}
              </SideNavMenu>
            );
          }
          // Vanlig lenke
          return (
            <SideNavLink key={item.href} href={item.href} icon={item.icon} active={isActive(item.href)}>
              {item.label}
            </SideNavLink>
          );
        })}
      </SideNav>

      {/* ================================================================= */}
      {/* 4. Side-innhold — rendres fra children (rute-spesifikt innhold)   */}
      {/* paddingLeft settes dynamisk via inline style: 3rem når rail er
          lukket, 16rem når rail er åpen. CSS-variabelen --shell-side-nav-width
          brukes som .content sin padding-left (ingen annen padding). */}
      <div
        className={styles.content}
        // data-pinned-page: globals.css gir html/body overflow-x: clip i stedet for hidden, ellers virker ikke position: sticky (pagineringen i tabellene)
        data-pinned-page=""
        data-nav={sideNavExpanded ? 'open' : 'closed'}
        style={
          {
            '--shell-side-nav-width': sideNavExpanded ? '16rem' : '3rem',
          } as React.CSSProperties
        }
      >
        {children}
      </div>
    </>
  );
}
